import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import Landing from "@/pages/Landing";
import CollectionDetail from "@/pages/CollectionDetail";
import Pricing from "@/pages/Pricing";
import PrivateAccess from "@/pages/PrivateAccess";
import Dashboard from "@/pages/Dashboard";

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.__lenis = lenis;
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="font-serif text-gold text-2xl tracking-wide-lux animate-pulse">Royce International</div>
      </div>
    );
  if (user === false) return <Navigate to="/private-access" replace />;
  return children;
}

function AppInner() {
  useLenis();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/collections/:category" element={<CollectionDetail />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/private-access" element={<PrivateAccess />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppInner />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#081A3A",
                  color: "#fff",
                  border: "1px solid rgba(197,139,42,0.4)",
                  fontFamily: "Inter, sans-serif",
                },
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
